import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  CreditCard, MagnifyingGlass, Receipt, CaretRight, Warning,
  CurrencyDollar, Clock, CheckCircle, XCircle, User, Buildings,
  Phone, Envelope, ArrowLeft, Plus, Funnel, CaretDown,
  Eye, FilePdf, ShareNetwork, WhatsappLogo, Handshake,
  ChatCircle, CalendarBlank, NotePencil,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

const STATUS_CONFIG = {
  vigente: { label: "Vigente", color: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  por_vencer: { label: "Por vencer", color: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  vencida: { label: "Vencida", color: "bg-red-100 text-red-700 border-red-200", dot: "bg-red-500" },
  pago_parcial: { label: "Pago parcial", color: "bg-blue-100 text-blue-700 border-blue-200", dot: "bg-blue-500" },
  pagada: { label: "Pagada", color: "bg-slate-100 text-slate-500 border-slate-200", dot: "bg-slate-400" },
};

const CREDIT_DAYS_OPTIONS = [7, 15, 30, 45, 60];

const fmtMoney = (v) => `$${(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.vigente;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.color}`} data-testid={`status-badge-${status}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ═══════════════════════════ MAIN COMPONENT ═══════════════════════════

export function POSCredits() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [activeView, setActiveView] = useState("dashboard"); // dashboard | accounts | expediente | cobranza
  const [selectedClient, setSelectedClient] = useState(null);

  const openExpediente = (clientId) => {
    setSelectedClient(clientId);
    setActiveView("expediente");
  };

  return (
    <div className="space-y-4" data-testid="cxc-module">
      {activeView === "dashboard" && (
        <CxCDashboard
          headers={headers}
          onViewAccounts={() => setActiveView("accounts")}
          onViewCobranza={() => setActiveView("cobranza")}
          onOpenExpediente={openExpediente}
        />
      )}
      {activeView === "accounts" && (
        <CxCAccountsList
          headers={headers}
          onBack={() => setActiveView("dashboard")}
          onOpenExpediente={openExpediente}
        />
      )}
      {activeView === "cobranza" && (
        <CollectionQueue
          headers={headers}
          onBack={() => setActiveView("dashboard")}
          onOpenExpediente={openExpediente}
        />
      )}
      {activeView === "expediente" && selectedClient && (
        <ClientExpediente
          headers={headers}
          clientId={selectedClient}
          onBack={() => setActiveView("dashboard")}
        />
      )}
    </div>
  );
}

// ═══════════════════════════ DASHBOARD ═══════════════════════════

function CxCDashboard({ headers, onViewAccounts, onViewCobranza, onOpenExpediente }) {
  const [stats, setStats] = useState(null);
  const [aging, setAging] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, agingRes, clientsRes] = await Promise.all([
          axios.get(`${API}/cxc/dashboard`, { headers }),
          axios.get(`${API}/cxc/aging`, { headers }),
          axios.get(`${API}/cxc/clients`, { headers }),
        ]);
        setStats(statsRes.data);
        setAging(agingRes.data);
        setClients(clientsRes.data);
      } catch { toast.error("Error al cargar dashboard CxC"); }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-sky-600 border-t-transparent rounded-full" /></div>;

  const debtors = clients.filter(c => c.total_due > 0).sort((a, b) => b.total_due - a.total_due);

  const handleExport = async (fmt) => {
    try {
      const res = await axios.get(`${API}/cxc/export/${fmt}`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cartera_CxC.${fmt === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Cartera exportada`);
    } catch { toast.error("Error al exportar"); }
  };

  return (
    <div className="space-y-6" data-testid="cxc-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CreditCard size={24} weight="duotone" className="text-sky-600" />
            Cuentas por Cobrar
          </h1>
          <p className="text-sm text-slate-500">Tablero de cartera y cobranza</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => handleExport("excel")} variant="outline" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 h-9 text-sm" data-testid="export-excel-btn">
            Excel
          </Button>
          <Button onClick={() => handleExport("pdf")} variant="outline" className="border-red-400 text-red-700 hover:bg-red-50 h-9 text-sm" data-testid="export-pdf-btn">
            <FilePdf size={14} className="mr-1" /> PDF
          </Button>
          <Button onClick={onViewCobranza} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50 h-9 text-sm" data-testid="view-cobranza-btn">
            <Phone size={14} className="mr-1" /> Cobranza
          </Button>
          <Button onClick={onViewAccounts} className="bg-sky-600 hover:bg-sky-700 text-white h-9 text-sm" data-testid="view-all-accounts-btn">
            Ver todas las cuentas <CaretRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="cxc-summary-cards">
          <SummaryCard label="Cartera Total" value={fmtMoney(stats.total_cartera)} icon={CurrencyDollar} color="sky" testId="card-total" />
          <SummaryCard label="Vencido" value={fmtMoney(stats.total_vencido)} icon={XCircle} color="red" testId="card-vencido" />
          <SummaryCard label="Por Vencer" value={fmtMoney(stats.total_por_vencer)} icon={Warning} color="amber" testId="card-por-vencer" />
          <SummaryCard label="Vigente" value={fmtMoney(stats.total_vigente)} icon={Clock} color="emerald" testId="card-vigente" />
          <SummaryCard label="Cobrado (Mes)" value={fmtMoney(stats.total_cobrado_mes)} icon={CheckCircle} color="violet" testId="card-cobrado" />
        </div>
      )}

      {/* Two columns: Aging + Top debtors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Aging Report */}
        {aging && (
          <div className="bg-white border rounded-xl p-5" data-testid="aging-report">
            <h3 className="text-sm font-bold text-slate-700 mb-4">Antiguedad de Saldos</h3>
            <div className="space-y-2.5">
              <AgingBar label="Corriente" value={aging.corriente} total={stats?.total_cartera} color="bg-emerald-500" />
              <AgingBar label="1-15 dias" value={aging["1_15"]} total={stats?.total_cartera} color="bg-amber-400" />
              <AgingBar label="16-30 dias" value={aging["16_30"]} total={stats?.total_cartera} color="bg-orange-500" />
              <AgingBar label="31-60 dias" value={aging["31_60"]} total={stats?.total_cartera} color="bg-red-500" />
              <AgingBar label="60+ dias" value={aging["61_plus"]} total={stats?.total_cartera} color="bg-red-700" />
            </div>
          </div>
        )}

        {/* Top Debtors */}
        <div className="bg-white border rounded-xl p-5" data-testid="top-debtors">
          <h3 className="text-sm font-bold text-slate-700 mb-4">Clientes con Saldo</h3>
          {debtors.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Sin cuentas pendientes</p>
          ) : (
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
              {debtors.slice(0, 15).map(c => (
                <button
                  key={c.id}
                  onClick={() => onOpenExpediente(c.id)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-sky-50 transition text-left group"
                  data-testid={`debtor-${c.id}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-bold shrink-0">
                      {(c.company_name || "?")[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-800 truncate">{c.company_name || c.contact_name}</p>
                      {c.overdue > 0 && <p className="text-[10px] text-red-500 font-bold">Vencido: {fmtMoney(c.overdue)}</p>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-slate-900 font-mono">{fmtMoney(c.total_due)}</p>
                    <CaretRight size={12} className="text-slate-300 group-hover:text-sky-500 ml-auto transition" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color, testId }) {
  const colorMap = {
    sky: "bg-sky-50 border-sky-200 text-sky-700",
    red: "bg-red-50 border-red-200 text-red-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    violet: "bg-violet-50 border-violet-200 text-violet-700",
  };
  const iconColor = {
    sky: "text-sky-500", red: "text-red-500", amber: "text-amber-500",
    emerald: "text-emerald-500", violet: "text-violet-500",
  };
  return (
    <div className={`rounded-xl border p-4 ${colorMap[color]}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} weight="duotone" className={iconColor[color]} />
        <span className="text-[10px] uppercase font-semibold tracking-wide opacity-80">{label}</span>
      </div>
      <p className="text-xl font-extrabold font-mono">{value}</p>
    </div>
  );
}

function AgingBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.min((value / total) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-500 w-20 shrink-0">{label}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
        <div className={`${color} h-full rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-bold text-slate-700 w-24 text-right font-mono">{fmtMoney(value)}</span>
    </div>
  );
}

// ═══════════════════════════ ACCOUNTS LIST ═══════════════════════════

function CxCAccountsList({ headers, onBack, onOpenExpediente }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("pendientes");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [payModal, setPayModal] = useState(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, pmRes] = await Promise.all([
        axios.get(`${API}/cxc/accounts?status_filter=${filter}&limit=300`, { headers }),
        axios.get(`${API}/pos/payment-methods`, { headers }),
      ]);
      setAccounts(accRes.data);
      setPaymentMethods(pmRes.data);
    } catch { toast.error("Error al cargar cuentas"); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const filtered = accounts.filter(a => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (a.folio || "").toLowerCase().includes(s) || (a.customer_name || "").toLowerCase().includes(s);
  });

  const totalBalance = filtered.reduce((s, a) => s + (a.balance || 0), 0);

  return (
    <div className="space-y-4" data-testid="cxc-accounts-list">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-sky-600 transition" data-testid="back-to-dashboard">
          <ArrowLeft size={16} /> Tablero
        </button>
        <h2 className="text-lg font-bold text-slate-900">Todas las Cuentas</h2>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar folio o cliente..." className="h-9 text-sm pl-8" data-testid="cxc-search" />
        </div>
        <div className="flex items-center gap-1.5">
          <Funnel size={14} className="text-slate-400" />
          {["pendientes", "vencidas", "por_vencer", "pagadas", "todas"].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition ${filter === f ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              data-testid={`filter-${f}`}
            >
              {f === "pendientes" ? "Pendientes" : f === "vencidas" ? "Vencidas" : f === "por_vencer" ? "Por Vencer" : f === "pagadas" ? "Pagadas" : "Todas"}
            </button>
          ))}
        </div>
        <div className="bg-sky-50 border border-sky-200 rounded-lg px-4 py-2 ml-auto">
          <p className="text-[10px] text-sky-600 uppercase font-semibold">Saldo Filtrado</p>
          <p className="text-lg font-extrabold text-sky-800 font-mono">{fmtMoney(totalBalance)}</p>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin w-7 h-7 border-4 border-sky-600 border-t-transparent rounded-full" /></div>
      ) : (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cxc-table">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-left">Folio</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-left">Cliente</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Vencimiento</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-right">Total</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-right">Pagado</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-right">Saldo</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Estado</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Accion</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.id} className={`border-b hover:bg-sky-50/30 transition ${a.cxc_status === "vencida" ? "bg-red-50/30" : ""}`}>
                    <td className="px-3 py-2.5 text-xs font-mono font-bold text-sky-700">
                      {a.public_token ? (
                        <a href={`/venta/${a.public_token}`} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1" data-testid={`folio-link-${a.id}`}>
                          {a.folio} <Eye size={11} className="text-slate-400" />
                        </a>
                      ) : a.folio}
                    </td>
                    <td className="px-3 py-2.5">
                      <button onClick={() => a.customer_id && onOpenExpediente(a.customer_id)} className="text-xs font-medium text-slate-800 hover:text-sky-600 transition text-left">
                        {a.customer_name}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className="text-xs text-slate-600">
                        {a.cxc_due_date ? format(new Date(a.cxc_due_date), "dd/MM/yy") : "-"}
                      </span>
                      {a.days_overdue > 0 && a.cxc_status !== "pagada" && (
                        <span className="ml-1 text-[9px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">{a.days_overdue}d</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtMoney(a.total)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-emerald-600">{fmtMoney(a.cxc_total_paid)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs font-bold text-slate-900">{fmtMoney(a.balance)}</td>
                    <td className="px-3 py-2.5 text-center"><StatusBadge status={a.cxc_status} /></td>
                    <td className="px-3 py-2.5 text-center">
                      {a.cxc_status !== "pagada" && a.balance > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-[11px] text-sky-700 border-sky-300 hover:bg-sky-50"
                          onClick={() => setPayModal(a)}
                          data-testid={`pay-btn-${a.id}`}
                        >
                          <CurrencyDollar size={12} className="mr-0.5" /> Abonar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Sin cuentas en este filtro</p>}
        </div>
      )}

      {/* Payment Modal */}
      {payModal && (
        <PaymentModal
          account={payModal}
          headers={headers}
          paymentMethods={paymentMethods}
          onClose={() => setPayModal(null)}
          onSuccess={() => { setPayModal(null); fetchAccounts(); }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════ PAYMENT MODAL ═══════════════════════════

function PaymentModal({ account, headers, paymentMethods, onClose, onSuccess }) {
  const [amount, setAmount] = useState(account.balance.toFixed(2));
  const [method, setMethod] = useState("efectivo");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null); // After payment: { payment, shareData }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error("Monto invalido"); return; }
    if (val > account.balance + 0.01) { toast.error("El monto excede el saldo"); return; }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/cxc/payments`, {
        sale_id: account.id,
        amount: val,
        payment_method: method,
        reference,
        notes,
      }, { headers });
      toast.success(`Pago de ${fmtMoney(val)} registrado para ${account.folio}`);

      // Get share URLs for the receipt
      const paymentData = res.data.payment;
      try {
        const shareRes = await axios.get(`${API}/cxc/payments/${paymentData.id}/share`, { headers });
        setReceipt({ payment: paymentData, shareData: shareRes.data });
      } catch {
        setReceipt({ payment: paymentData, shareData: null });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar pago");
    }
    setSaving(false);
  };

  const handleClose = () => {
    if (receipt) onSuccess();
    else onClose();
  };

  // ── Receipt confirmation view ──
  if (receipt) {
    const p = receipt.payment;
    const share = receipt.shareData;
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={handleClose}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="receipt-confirmation">
          <div className="bg-emerald-600 p-5 text-center rounded-t-2xl">
            <CheckCircle size={36} weight="fill" className="text-white mx-auto mb-2" />
            <p className="text-white font-bold text-lg">Abono Registrado</p>
          </div>
          <div className="p-5 space-y-3">
            <div className="text-center py-3">
              <p className="text-3xl font-extrabold text-emerald-700 font-mono">{fmtMoney(p.amount)}</p>
              <p className="text-xs text-slate-500 mt-1">Folio: {p.sale_folio} &middot; {p.customer_name}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Saldo anterior:</span><span className="font-mono">{fmtMoney(p.balance_before)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Abono:</span><span className="font-mono font-bold text-emerald-600">-{fmtMoney(p.amount)}</span></div>
              <div className="flex justify-between border-t pt-1.5"><span className="text-slate-500 font-bold">Saldo restante:</span><span className="font-mono font-bold">{fmtMoney(p.balance_after)}</span></div>
            </div>

            {/* Action buttons */}
            <div className="space-y-2 pt-2">
              {share?.public_url && (
                <a
                  href={share.public_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-9 text-sm font-bold bg-sky-600 hover:bg-sky-700 text-white rounded-md flex items-center justify-center gap-2 transition"
                  data-testid="view-receipt-btn"
                >
                  <Eye size={16} /> Ver Comprobante
                </a>
              )}
              {share?.pdf_url && (
                <a
                  href={share.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-9 text-sm font-bold bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center justify-center gap-2 transition"
                  data-testid="download-receipt-pdf"
                >
                  <FilePdf size={16} /> Descargar PDF
                </a>
              )}
              {share?.whatsapp_url && (
                <a
                  href={share.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-9 text-sm font-bold bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center justify-center gap-2 transition"
                  data-testid="share-receipt-whatsapp"
                >
                  <WhatsappLogo size={16} weight="fill" /> Compartir por WhatsApp
                </a>
              )}
              <Button variant="outline" onClick={handleClose} className="w-full h-9 text-sm" data-testid="close-receipt-btn">
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Payment form ──
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="payment-modal">
        <div className="p-5 border-b">
          <h3 className="text-base font-bold text-slate-900">Registrar Abono</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {account.folio} &middot; {account.customer_name} &middot; Saldo: <span className="font-bold text-red-600">{fmtMoney(account.balance)}</span>
          </p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Monto del Abono *</label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max={account.balance}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className="h-10 text-lg font-mono font-bold"
              data-testid="payment-amount"
              autoFocus
            />
            <div className="flex gap-2 mt-1.5">
              <button type="button" onClick={() => setAmount(account.balance.toFixed(2))} className="text-[10px] text-sky-600 font-bold hover:underline">Total</button>
              <button type="button" onClick={() => setAmount((account.balance / 2).toFixed(2))} className="text-[10px] text-sky-600 font-bold hover:underline">50%</button>
              <button type="button" onClick={() => setAmount((account.balance / 4).toFixed(2))} className="text-[10px] text-sky-600 font-bold hover:underline">25%</button>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Metodo de Pago</label>
            <select
              value={method}
              onChange={e => setMethod(e.target.value)}
              className="w-full h-9 border rounded-md text-sm px-3"
              data-testid="payment-method"
            >
              {paymentMethods.map(pm => (
                <option key={pm.id} value={pm.id}>{pm.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Referencia</label>
            <Input value={reference} onChange={e => setReference(e.target.value)} placeholder="No. transferencia, cheque, etc." className="h-9 text-sm" data-testid="payment-reference" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Notas</label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observaciones..." className="text-sm" rows={2} data-testid="payment-notes" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-9 text-sm">Cancelar</Button>
            <Button type="submit" disabled={saving} className="flex-1 h-9 text-sm bg-sky-600 hover:bg-sky-700 text-white" data-testid="confirm-payment-btn">
              {saving ? "Registrando..." : `Registrar ${fmtMoney(parseFloat(amount) || 0)}`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════ COLLECTION QUEUE (BANDEJA DE COBRANZA) ═══════════════════════════

function CollectionQueue({ headers, onBack, onOpenExpediente }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interactionModal, setInteractionModal] = useState(null);
  const [promiseModal, setPromiseModal] = useState(null);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cxc/collection-queue`, { headers });
      setItems(res.data);
    } catch { toast.error("Error al cargar bandeja"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const URGENCY_STYLES = {
    critica: "border-l-4 border-l-red-500 bg-red-50/40",
    vencida: "border-l-4 border-l-orange-500 bg-orange-50/30",
    por_vencer: "border-l-4 border-l-amber-400 bg-amber-50/20",
    normal: "border-l-4 border-l-emerald-400",
  };
  const URGENCY_BADGE = {
    critica: "bg-red-100 text-red-700",
    vencida: "bg-orange-100 text-orange-700",
    por_vencer: "bg-amber-100 text-amber-700",
    normal: "bg-emerald-100 text-emerald-700",
  };

  return (
    <div className="space-y-4" data-testid="collection-queue">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-sky-600 transition"><ArrowLeft size={16} /> Tablero</button>
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Phone size={20} weight="duotone" className="text-amber-600" /> Bandeja de Cobranza</h2>
        <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full text-slate-600 font-semibold">{items.length} cuentas</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin w-7 h-7 border-4 border-amber-600 border-t-transparent rounded-full" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <CheckCircle size={48} className="mx-auto mb-3 text-emerald-400" />
          <p className="font-bold text-emerald-600">Sin cuentas pendientes de cobro</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.id} className={`bg-white border rounded-xl p-4 ${URGENCY_STYLES[item.urgency] || ""}`} data-testid={`queue-item-${item.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {/* Left: Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <button onClick={() => item.customer_id && onOpenExpediente(item.customer_id)} className="text-sm font-bold text-slate-900 hover:text-sky-600 transition">{item.customer_name}</button>
                    <span className="text-xs font-mono text-sky-700 font-bold">{item.folio}</span>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${URGENCY_BADGE[item.urgency]}`}>
                      {item.urgency === "critica" ? "CRITICA" : item.urgency === "vencida" ? "VENCIDA" : item.urgency === "por_vencer" ? "POR VENCER" : "VIGENTE"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>Saldo: <strong className="text-slate-900 font-mono">{fmtMoney(item.balance)}</strong></span>
                    <span>Vence: <strong>{item.cxc_due_date ? format(new Date(item.cxc_due_date), "dd/MM/yy") : "-"}</strong></span>
                    {item.days_overdue > 0 && <span className="text-red-600 font-bold">{item.days_overdue} dias vencida</span>}
                  </div>
                  {/* Last interaction & promise */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[10px]">
                    {item.last_interaction && (
                      <span className="text-slate-400">Ultima gestion: <strong className="text-slate-600 capitalize">{item.last_interaction.type}</strong> {item.last_interaction.date ? format(new Date(item.last_interaction.date), "dd/MM") : ""}</span>
                    )}
                    {item.active_promise && (
                      <span className="text-violet-600 font-bold">Promesa: {fmtMoney(item.active_promise.amount)} para {item.active_promise.promise_date ? format(new Date(item.active_promise.promise_date), "dd/MM/yy") : "?"}</span>
                    )}
                    {item.next_action_date && (
                      <span className="text-sky-600">Proxima accion: {format(new Date(item.next_action_date), "dd/MM/yy")} - {item.next_action_note}</span>
                    )}
                  </div>
                </div>
                {/* Right: Actions */}
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 text-[10px] text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => setInteractionModal(item)} data-testid={`log-interaction-${item.id}`}>
                    <ChatCircle size={12} className="mr-0.5" /> Gestion
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-[10px] text-violet-700 border-violet-300 hover:bg-violet-50" onClick={() => setPromiseModal(item)} data-testid={`log-promise-${item.id}`}>
                    <Handshake size={12} className="mr-0.5" /> Promesa
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Interaction Modal */}
      {interactionModal && (
        <InteractionModal
          account={interactionModal}
          headers={headers}
          onClose={() => setInteractionModal(null)}
          onSuccess={() => { setInteractionModal(null); fetchQueue(); }}
        />
      )}
      {/* Promise Modal */}
      {promiseModal && (
        <PromiseModal
          account={promiseModal}
          headers={headers}
          onClose={() => setPromiseModal(null)}
          onSuccess={() => { setPromiseModal(null); fetchQueue(); }}
        />
      )}
    </div>
  );
}

function InteractionModal({ account, headers, onClose, onSuccess }) {
  const [type, setType] = useState("llamada");
  const [notes, setNotes] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [nextNote, setNextNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!notes.trim()) { toast.error("Agrega una nota"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/cxc/interactions`, {
        sale_id: account.id,
        interaction_type: type,
        notes,
        next_action_date: nextDate || null,
        next_action_note: nextNote,
      }, { headers });
      toast.success("Gestion registrada");
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="interaction-modal">
        <div className="bg-amber-600 p-4 text-white rounded-t-2xl">
          <p className="font-bold text-sm">Registrar Gestion de Cobranza</p>
          <p className="text-xs opacity-80 mt-0.5">{account.folio} &middot; {account.customer_name} &middot; Saldo: {fmtMoney(account.balance)}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Tipo de Gestion</label>
            <div className="flex gap-1.5 flex-wrap">
              {[{v:"llamada",l:"Llamada"},{v:"email",l:"Email"},{v:"whatsapp",l:"WhatsApp"},{v:"visita",l:"Visita"},{v:"otro",l:"Otro"}].map(t => (
                <button key={t.v} type="button" onClick={() => setType(t.v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${type === t.v ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  data-testid={`interaction-type-${t.v}`}>{t.l}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Notas de la Gestion *</label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Resultado de la llamada, acuerdos, etc." className="text-sm" rows={3} data-testid="interaction-notes" autoFocus />
          </div>
          <div className="border-t pt-3">
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Proxima Accion (opcional)</label>
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} className="h-8 text-xs" data-testid="next-action-date" />
              <Input value={nextNote} onChange={e => setNextNote(e.target.value)} placeholder="Volver a llamar..." className="h-8 text-xs" data-testid="next-action-note" />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-9 text-sm">Cancelar</Button>
            <Button type="submit" disabled={saving} className="flex-1 h-9 text-sm bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-interaction-btn">
              {saving ? "Guardando..." : "Registrar Gestion"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PromiseModal({ account, headers, onClose, onSuccess }) {
  const [promiseDate, setPromiseDate] = useState("");
  const [amount, setAmount] = useState(account.balance?.toFixed(2) || "0");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!promiseDate) { toast.error("Selecciona fecha"); return; }
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error("Monto invalido"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/cxc/promises`, {
        sale_id: account.id,
        customer_id: account.customer_id,
        promise_date: promiseDate,
        amount: val,
        notes,
      }, { headers });
      toast.success("Promesa de pago registrada");
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="promise-modal">
        <div className="bg-violet-600 p-4 text-white rounded-t-2xl">
          <p className="font-bold text-sm">Registrar Promesa de Pago</p>
          <p className="text-xs opacity-80 mt-0.5">{account.folio} &middot; {account.customer_name}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Fecha Prometida *</label>
            <Input type="date" value={promiseDate} onChange={e => setPromiseDate(e.target.value)} className="h-9 text-sm" data-testid="promise-date" autoFocus />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Monto Prometido *</label>
            <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="h-9 text-sm font-mono" data-testid="promise-amount" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Notas</label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Detalles del compromiso..." className="text-sm" rows={2} data-testid="promise-notes" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-9 text-sm">Cancelar</Button>
            <Button type="submit" disabled={saving} className="flex-1 h-9 text-sm bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-promise-btn">
              {saving ? "Guardando..." : "Registrar Promesa"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════ PAYMENT HISTORY ROW ═══════════════════════════

function PaymentHistoryRow({ payment: p, headers }) {
  const [shareData, setShareData] = useState(null);
  const [showActions, setShowActions] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadShareUrls = async () => {
    if (shareData) { setShowActions(true); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cxc/payments/${p.id}/share`, { headers });
      setShareData(res.data);
      setShowActions(true);
    } catch { toast.error("Error al obtener datos del comprobante"); }
    setLoading(false);
  };

  return (
    <tr className="border-b hover:bg-slate-50/50" data-testid={`payment-row-${p.id}`}>
      <td className="px-3 py-2 text-xs text-slate-600">{p.created_at ? format(new Date(p.created_at), "dd/MM/yy HH:mm") : ""}</td>
      <td className="px-3 py-2 text-xs font-mono text-sky-700 font-bold">{p.sale_folio}</td>
      <td className="px-3 py-2 text-xs text-slate-700 capitalize">{(p.payment_method || "").replace(/_/g, " ")}</td>
      <td className="px-3 py-2 text-xs text-slate-500">{p.reference || "-"}</td>
      <td className="px-3 py-2 text-right font-mono text-xs font-bold text-emerald-600">{fmtMoney(p.amount)}</td>
      <td className="px-3 py-2 text-right font-mono text-xs">{fmtMoney(p.balance_after)}</td>
      <td className="px-3 py-2 text-center relative">
        <Button
          size="sm"
          variant="outline"
          className="h-6 text-[10px] text-sky-700 border-sky-300 hover:bg-sky-50"
          onClick={loadShareUrls}
          disabled={loading}
          data-testid={`share-payment-${p.id}`}
        >
          {loading ? "..." : <><ShareNetwork size={11} className="mr-0.5" /> Compartir</>}
        </Button>
        {showActions && shareData && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setShowActions(false)} />
            <div className="absolute right-0 top-full mt-1 z-40 bg-white rounded-xl shadow-xl border p-2.5 w-48 space-y-1.5" data-testid={`share-menu-${p.id}`}>
              <a
                href={shareData.public_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-sky-50 text-xs font-semibold text-sky-700 transition w-full"
                data-testid={`view-receipt-${p.id}`}
              >
                <Eye size={14} /> Ver Comprobante
              </a>
              <a
                href={shareData.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-red-50 text-xs font-semibold text-red-600 transition w-full"
                data-testid={`pdf-receipt-${p.id}`}
              >
                <FilePdf size={14} /> Descargar PDF
              </a>
              <a
                href={shareData.whatsapp_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-green-50 text-xs font-semibold text-green-700 transition w-full"
                data-testid={`whatsapp-receipt-${p.id}`}
              >
                <WhatsappLogo size={14} weight="fill" /> WhatsApp
              </a>
            </div>
          </>
        )}
      </td>
    </tr>
  );
}

// ═══════════════════════════ CLIENT EXPEDIENTE ═══════════════════════════

function ClientExpediente({ headers, clientId, onBack }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editCredit, setEditCredit] = useState(false);
  const [creditForm, setCreditForm] = useState({ credit_limit: 0, credit_days: 30, credit_enabled: false });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [payModal, setPayModal] = useState(null);
  const [activeTab, setActiveTab] = useState("cuentas");
  const [portalData, setPortalData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [expRes, pmRes] = await Promise.all([
        axios.get(`${API}/cxc/clients/${clientId}`, { headers }),
        axios.get(`${API}/pos/payment-methods`, { headers }),
      ]);
      setData(expRes.data);
      setPaymentMethods(pmRes.data);
      const c = expRes.data.client;
      setCreditForm({
        credit_limit: c.credit_limit || 0,
        credit_days: c.credit_days || 30,
        credit_enabled: c.credit_enabled || false,
      });
    } catch { toast.error("Error al cargar expediente"); }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveCredit = async () => {
    try {
      await axios.put(`${API}/cxc/clients/${clientId}/credit`, creditForm, { headers });
      toast.success("Credito actualizado");
      setEditCredit(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-sky-600 border-t-transparent rounded-full" /></div>;
  if (!data) return <p className="text-center text-slate-500 py-12">No se encontro el expediente</p>;

  const { client, summary, accounts, payments, promises = [], interactions = [], purchase_history } = data;
  const isAdmin = user?.role === "admin";

  const generatePortalLink = async () => {
    try {
      const res = await axios.post(`${API}/cxc/clients/${clientId}/portal-link`, {}, { headers });
      setPortalData(res.data);
      toast.success("Enlace de estado de cuenta generado");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al generar enlace"); }
  };

  return (
    <div className="space-y-5" data-testid="client-expediente">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-sky-600 transition" data-testid="back-from-expediente">
          <ArrowLeft size={16} /> Tablero
        </button>
      </div>

      {/* Client Info Card */}
      <div className="bg-white border rounded-xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-sky-100 flex items-center justify-center text-sky-700 text-xl font-bold shrink-0">
            {(client.company_name || "?")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-slate-900">{client.company_name}</h2>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-500">
              {client.contact_name && <span className="flex items-center gap-1"><User size={12} />{client.contact_name}</span>}
              {client.phone && <span className="flex items-center gap-1"><Phone size={12} />{client.phone}</span>}
              {client.email && <span className="flex items-center gap-1"><Envelope size={12} />{client.email}</span>}
              {client.city && <span className="flex items-center gap-1"><Buildings size={12} />{client.city}{client.state ? `, ${client.state}` : ""}</span>}
            </div>
            {client.credit_blocked && (
              <Badge className="mt-2 bg-red-100 text-red-700 border-red-200 text-[10px]">BLOQUEADO</Badge>
            )}
          </div>

          {/* Summary boxes */}
          <div className="flex gap-2 flex-wrap sm:flex-nowrap">
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-center min-w-[100px]">
              <p className="text-[9px] text-red-500 uppercase font-bold">Saldo</p>
              <p className="text-base font-extrabold text-red-700 font-mono">{fmtMoney(summary.total_due)}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-center min-w-[100px]">
              <p className="text-[9px] text-amber-500 uppercase font-bold">Vencido</p>
              <p className="text-base font-extrabold text-amber-700 font-mono">{fmtMoney(summary.overdue)}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-center min-w-[100px]">
              <p className="text-[9px] text-emerald-500 uppercase font-bold">Disponible</p>
              <p className="text-base font-extrabold text-emerald-700 font-mono">{fmtMoney(summary.available_credit)}</p>
            </div>
          </div>
        </div>

        {/* Portal / Estado de Cuenta */}
        <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs text-sky-700 border-sky-300 hover:bg-sky-50 px-3" onClick={generatePortalLink} data-testid="generate-portal-btn">
            <ShareNetwork size={14} className="mr-1.5" /> Enviar Estado de Cuenta
          </Button>
          {portalData && (
            <>
              <a href={portalData.public_url} target="_blank" rel="noopener noreferrer" className="text-[10px] font-bold text-sky-600 hover:underline flex items-center gap-1" data-testid="portal-link">
                <Eye size={12} /> Ver Portal
              </a>
              <a href={portalData.whatsapp_url} target="_blank" rel="noopener noreferrer" className="text-[10px] font-bold text-green-600 hover:underline flex items-center gap-1" data-testid="portal-whatsapp">
                <WhatsappLogo size={12} weight="fill" /> WhatsApp
              </a>
              <button onClick={() => { navigator.clipboard.writeText(portalData.public_url); toast.success("Link copiado"); }} className="text-[10px] font-bold text-slate-500 hover:underline" data-testid="portal-copy">
                Copiar Link
              </button>
            </>
          )}
        </div>

        {/* Credit Config */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wide">Condiciones de Credito</h4>
            {isAdmin && !editCredit && (
              <Button size="sm" variant="ghost" className="text-xs text-sky-600" onClick={() => setEditCredit(true)} data-testid="edit-credit-btn">Editar</Button>
            )}
          </div>
          {editCredit ? (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] text-slate-500 font-semibold mb-0.5 block">Limite de Credito</label>
                <Input
                  type="number"
                  value={creditForm.credit_limit}
                  onChange={e => setCreditForm(p => ({ ...p, credit_limit: parseFloat(e.target.value) || 0 }))}
                  className="h-8 text-sm"
                  data-testid="credit-limit-input"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-semibold mb-0.5 block">Plazo (dias)</label>
                <select
                  value={creditForm.credit_days}
                  onChange={e => setCreditForm(p => ({ ...p, credit_days: parseInt(e.target.value) }))}
                  className="w-full h-8 border rounded-md text-sm px-2"
                  data-testid="credit-days-select"
                >
                  {CREDIT_DAYS_OPTIONS.map(d => <option key={d} value={d}>{d} dias</option>)}
                </select>
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={creditForm.credit_enabled}
                    onChange={e => setCreditForm(p => ({ ...p, credit_enabled: e.target.checked }))}
                    className="w-4 h-4 rounded"
                    data-testid="credit-enabled-checkbox"
                  />
                  <span className="text-xs text-slate-700">Credito Activo</span>
                </label>
              </div>
              <div className="sm:col-span-3 flex gap-2">
                <Button size="sm" onClick={saveCredit} className="bg-sky-600 hover:bg-sky-700 text-white text-xs h-8" data-testid="save-credit-btn">Guardar</Button>
                <Button size="sm" variant="outline" onClick={() => setEditCredit(false)} className="text-xs h-8">Cancelar</Button>
              </div>
            </div>
          ) : (
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600">
              <span>Limite: <strong className="text-slate-900">{fmtMoney(client.credit_limit)}</strong></span>
              <span>Plazo: <strong className="text-slate-900">{client.credit_days} dias</strong></span>
              <span>Estado: <strong className={client.credit_enabled ? "text-emerald-600" : "text-slate-400"}>{client.credit_enabled ? "Activo" : "Inactivo"}</strong></span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { id: "cuentas", label: "Cuentas Pendientes", count: accounts.filter(a => a.balance > 0).length },
          { id: "pagos", label: "Historial de Pagos", count: payments.length },
          { id: "promesas", label: "Promesas", count: promises.length },
          { id: "gestiones", label: "Gestiones", count: interactions.length },
          { id: "compras", label: "Compras", count: purchase_history.length },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2.5 text-xs font-semibold transition border-b-2 ${activeTab === t.id ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`tab-${t.id}`}
          >
            {t.label} <span className="ml-1 text-[10px] bg-slate-100 px-1.5 py-0.5 rounded-full">{t.count}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "cuentas" && (
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="expediente-accounts">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Folio</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-center">Vencimiento</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-right">Total</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-right">Pagado</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-right">Saldo</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-center">Estado</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-center">Accion</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.id} className={`border-b ${a.cxc_status === "vencida" ? "bg-red-50/40" : ""}`}>
                  <td className="px-3 py-2 text-xs font-mono font-bold text-sky-700">
                    {a.public_token ? (
                      <a href={`/venta/${a.public_token}`} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1">
                        {a.folio} <Eye size={11} className="text-slate-400" />
                      </a>
                    ) : a.folio}
                  </td>
                  <td className="px-3 py-2 text-center text-xs text-slate-600">
                    {a.cxc_due_date ? format(new Date(a.cxc_due_date), "dd/MM/yy") : "-"}
                    {a.days_overdue > 0 && a.cxc_status !== "pagada" && <span className="ml-1 text-[9px] font-bold text-red-600 bg-red-100 px-1 py-0.5 rounded">{a.days_overdue}d</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{fmtMoney(a.total)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-emerald-600">{fmtMoney(a.cxc_total_paid)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs font-bold">{fmtMoney(a.balance)}</td>
                  <td className="px-3 py-2 text-center"><StatusBadge status={a.cxc_status} /></td>
                  <td className="px-3 py-2 text-center">
                    {a.cxc_status !== "pagada" && a.balance > 0 && (
                      <Button size="sm" variant="outline" className="h-6 text-[10px] text-sky-700 border-sky-300" onClick={() => setPayModal(a)} data-testid={`exp-pay-${a.id}`}>
                        Abonar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {accounts.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Sin cuentas registradas</p>}
        </div>
      )}

      {activeTab === "pagos" && (
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="expediente-payments">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Fecha</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Folio</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Metodo</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Referencia</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-right">Monto</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-right">Saldo Despues</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-center">Comprobante</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <PaymentHistoryRow key={p.id} payment={p} headers={headers} />
              ))}
            </tbody>
          </table>
          {payments.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Sin pagos registrados</p>}
        </div>
      )}

      {activeTab === "promesas" && (
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="expediente-promises">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Folio</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-center">Fecha Promesa</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-right">Monto</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Notas</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-center">Estado</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Registrado</th>
              </tr>
            </thead>
            <tbody>
              {promises.map(p => {
                const statusCfg = { pendiente: "bg-amber-100 text-amber-700", cumplida: "bg-emerald-100 text-emerald-700", incumplida: "bg-red-100 text-red-700" };
                return (
                  <tr key={p.id} className="border-b">
                    <td className="px-3 py-2 text-xs font-mono text-sky-700 font-bold">{p.sale_folio}</td>
                    <td className="px-3 py-2 text-center text-xs">{p.promise_date ? format(new Date(p.promise_date), "dd/MM/yy") : "-"}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs font-bold">{fmtMoney(p.amount)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 max-w-[200px] truncate">{p.notes || "-"}</td>
                    <td className="px-3 py-2 text-center"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusCfg[p.status] || ""}`}>{p.status}</span></td>
                    <td className="px-3 py-2 text-xs text-slate-500">{p.created_by_name} {p.created_at ? format(new Date(p.created_at), "dd/MM") : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {promises.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Sin promesas registradas</p>}
        </div>
      )}

      {activeTab === "gestiones" && (
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="expediente-interactions">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Fecha</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Folio</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-center">Tipo</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Notas</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Registrado por</th>
              </tr>
            </thead>
            <tbody>
              {interactions.map(i => {
                const typeCfg = { llamada: "bg-blue-100 text-blue-700", email: "bg-purple-100 text-purple-700", whatsapp: "bg-green-100 text-green-700", visita: "bg-amber-100 text-amber-700", otro: "bg-slate-100 text-slate-700" };
                return (
                  <tr key={i.id} className="border-b">
                    <td className="px-3 py-2 text-xs text-slate-600">{i.created_at ? format(new Date(i.created_at), "dd/MM/yy HH:mm") : ""}</td>
                    <td className="px-3 py-2 text-xs font-mono text-sky-700 font-bold">{i.sale_folio}</td>
                    <td className="px-3 py-2 text-center"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${typeCfg[i.interaction_type] || ""}`}>{i.interaction_type}</span></td>
                    <td className="px-3 py-2 text-xs text-slate-700 max-w-[300px]">{i.notes}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{i.created_by_name}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {interactions.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Sin gestiones registradas</p>}
        </div>
      )}

      {activeTab === "compras" && (
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="expediente-purchases">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Folio</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-left">Fecha</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-center">Tipo</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-right">Total</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600 text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              {purchase_history.map(s => (
                <tr key={s.id} className="border-b">
                  <td className="px-3 py-2 text-xs font-mono font-bold text-slate-700">{s.folio}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{s.created_at ? format(new Date(s.created_at), "dd/MM/yy") : ""}</td>
                  <td className="px-3 py-2 text-center text-[10px] font-bold text-slate-500 uppercase">{s.doc_type}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs font-bold">{fmtMoney(s.total)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.status === "completada" ? "bg-emerald-100 text-emerald-700" : s.status === "pendiente" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                      {s.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {purchase_history.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Sin compras registradas</p>}
        </div>
      )}

      {/* Payment Modal */}
      {payModal && (
        <PaymentModal
          account={payModal}
          headers={headers}
          paymentMethods={paymentMethods}
          onClose={() => setPayModal(null)}
          onSuccess={() => { setPayModal(null); fetchData(); }}
        />
      )}
    </div>
  );
}
